// v10/src/components/TourGuide.js

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

// ── Pasos del tour ────────────────────────────────────────────────
const STEPS = [
  {
    id:       'welcome',
    title:    '👋 Bienvenido a Condor360',
    content:  'Tu asistente digital de gestión aeronáutica. En unos pasos te mostramos todo lo que podés hacer.',
    selector: null,
    position: 'center',
  },
  {
    id:       'aircraft',
    title:    '🛩️ Seleccioná tu aeronave',
    content:  'Elegí el avión que vas a volar. Solo ves las aeronaves asignadas a tu usuario por el administrador del aeroclub. También podés escanear el código QR pegado en la aeronave para acceder directo.',
    selector: null,
    position: 'center',
  },
  {
    id:       'checklist',
    title:    '✅ Chequeo prevuelo guiado',
    content:  'Tocá cada ítem para marcarlo como verificado. Si omitís alguno, quedará marcado como pendiente para que lo revisés antes de declarar el chequeo completo.',
    selector: null,
    position: 'center',
  },
  {
    id:       'voice',
    title:    '🎙️ Valentina, tu copiloto IA',
    content:  'Valentina lee cada ítem en voz alta para que puedas hacer el chequeo sin mirar la pantalla. Decí "listo" para confirmar o tocá el ítem directamente. Funciona offline si los audios están descargados.',
    selector: null,
    position: 'center',
  },
  {
    id:       'observations',
    title:    '💬 Observaciones',
    content:  'Si detectás algo fuera de lo normal en un ítem, podés agregar una observación con texto y foto. El instructor o admin recibe una alerta y puede autorizar o retener el vuelo.',
    selector: null,
    position: 'center',
  },
  {
    id:       'hobbs',
    title:    '⏱️ Horómetro post-vuelo',
    content:  'Al aterrizar, abrí la app y registrá el horómetro final. También podés anotar el combustible restante y observaciones del vuelo. Las horas se calculan automáticamente.',
    selector: null,
    position: 'center',
  },
  {
    id:       'flightlog',
    title:    '📖 Libro de vuelos digital',
    content:  'Desde el menú (☰) → "Mis vuelos" accedés a tu historial de vuelos por mes y tus estadísticas anuales. Podés exportarlo en PDF, Excel o CSV.',
    selector: null,
    position: 'center',
  },
  {
    id:       'turnos',
    title:    '📅 Turnos de instrucción',
    content:  'Desde el menú (☰) → "Mis turnos" podés ver los slots disponibles de tus instructores y reservar un turno. Recibís un recordatorio automático el día anterior.',
    selector: null,
    position: 'center',
  },
  {
    id:       'flowmode',
    title:    '🌊 Flow Mode',
    content:  'Para pilotos con experiencia: el Flow Mode te guía por el chequeo con ítems trampa para evaluar tu atención. Funciona completamente offline con la voz de Valentina.',
    selector: null,
    position: 'center',
  },
  {
    id:       'offline',
    title:    '📡 Funciona sin internet',
    content:  'Condor360 funciona offline una vez que los audios estén descargados. El chequeo, el horómetro y las observaciones se guardan localmente y se sincronizan cuando volvés a tener conexión.',
    selector: null,
    position: 'center',
  },
  {
    id:       'complete',
    title:    '🎉 ¡Listo para volar!',
    content:  'Ya conocés las funciones principales. Podés volver a ver esta guía desde el menú (☰) → "Cómo usar la app". ¡Buen vuelo! 🛫',
    selector: null,
    position: 'center',
  },
];

// ── Overlay con spotlight ─────────────────────────────────────────
const Spotlight = ({ rect }) => {
  if (!rect) return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 9000, pointerEvents: 'none' }} />
  );
  const PAD = 10;
  return (
    <svg style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', zIndex: 9000, pointerEvents: 'none' }}>
      <defs>
        <mask id="spotlight-mask">
          <rect width="100%" height="100%" fill="white" />
          <rect x={rect.left - PAD} y={rect.top - PAD} width={rect.width + PAD * 2} height={rect.height + PAD * 2} rx="12" fill="black" />
        </mask>
      </defs>
      <rect width="100%" height="100%" fill="rgba(0,0,0,0.72)" mask="url(#spotlight-mask)" />
      <rect x={rect.left - PAD} y={rect.top - PAD} width={rect.width + PAD * 2} height={rect.height + PAD * 2} rx="12" fill="none" stroke="rgba(99,102,241,0.85)" strokeWidth="2.5" />
    </svg>
  );
};

// ── Contenido interno del tooltip ─────────────────────────────────
const TooltipContent = ({ step, stepIndex, total, onNext, onPrev, onSkip, darkMode }) => (
  <motion.div
    key={stepIndex}
    initial={{ opacity: 0, scale: 0.92, y: 8 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.9, y: -6 }}
    transition={{ duration: 0.22 }}
    style={{
      width: '100%',
      background: darkMode ? 'rgba(12,18,34,0.97)' : 'white',
      border: darkMode ? '1px solid rgba(99,102,241,0.35)' : '1px solid rgba(99,102,241,0.2)',
      borderRadius: '24px',
      padding: window.innerWidth > 768 ? '28px 32px' : '20px',
      boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
    }}
  >
    <div style={{ display: 'flex', gap: '4px', marginBottom: '14px' }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          height: '3px', flex: 1, borderRadius: '2px', transition: 'background 0.3s',
          background: i <= stepIndex
            ? 'linear-gradient(90deg,#4f46e5,#7c3aed)'
            : darkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
        }} />
      ))}
    </div>

    <h3 style={{ margin: '0 0 10px', fontSize: window.innerWidth > 768 ? '18px' : '15px', fontWeight: 800, color: darkMode ? 'white' : '#0f172a' }}>
      {step.title}
    </h3>
    <p style={{ margin: '0 0 22px', fontSize: window.innerWidth > 768 ? '14px' : '13px', lineHeight: 1.7, color: darkMode ? 'rgba(255,255,255,0.55)' : '#64748b' }}>
      {step.content}
    </p>

    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      {stepIndex > 0 && (
        <button onClick={onPrev} style={{
          padding: '8px 14px', borderRadius: '12px', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          background: 'transparent', color: darkMode ? 'rgba(255,255,255,0.45)' : '#64748b',
          cursor: 'pointer', fontWeight: 600, fontSize: '13px',
          display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'inherit',
        }}>
          <ChevronLeft style={{ width: '13px', height: '13px' }} /> Atrás
        </button>
      )}
      <button onClick={onNext} style={{
        flex: 1, padding: '10px 16px', borderRadius: '12px', border: 'none',
        background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: 'white',
        cursor: 'pointer', fontWeight: 700, fontSize: '14px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontFamily: 'inherit',
      }}>
        {stepIndex < total - 1
          ? <><span>Siguiente</span><ChevronRight style={{ width: '14px', height: '14px' }} /></>
          : <span>¡Listo! 🎉</span>
        }
      </button>
      <button onClick={onSkip} title="Saltar tour" style={{
        padding: '8px', borderRadius: '10px', border: 'none', background: 'transparent',
        cursor: 'pointer', color: darkMode ? 'rgba(255,255,255,0.25)' : '#94a3b8', display: 'flex',
      }}>
        <X style={{ width: '15px', height: '15px' }} />
      </button>
    </div>

    <p style={{ margin: '10px 0 0', textAlign: 'center', fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.18)' : '#cbd5e1' }}>
      {stepIndex + 1} / {total}
    </p>
  </motion.div>
);

// ── Tooltip con posicionamiento ───────────────────────────────────
const TOOLTIP_W = 480;
const TOOLTIP_H = 200;

const Tooltip = ({ step, stepIndex, total, rect, onNext, onPrev, onSkip, darkMode }) => {
  const isCenter = !rect || step.position === 'center';

  // Pasos centrados: wrapper flex que cubre todo el viewport — sin cálculos
  if (isCenter) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9001,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        pointerEvents: 'none',
      }}>
        <div style={{ width: '100%', maxWidth: '520px', pointerEvents: 'all' }}>
          <TooltipContent
            step={step} stepIndex={stepIndex} total={total}
            onNext={onNext} onPrev={onPrev} onSkip={onSkip} darkMode={darkMode}
          />
        </div>
      </div>
    );
  }

  // Pasos con elemento target: posicionamiento calculado
  let posStyle = {};
  if (step.position === 'bottom') {
    posStyle = {
      top:  Math.min(rect.bottom + 16, window.innerHeight - TOOLTIP_H - 16),
      left: Math.max(16, Math.min(rect.left + rect.width / 2 - TOOLTIP_W / 2, window.innerWidth - TOOLTIP_W - 16)),
    };
  } else if (step.position === 'right') {
    posStyle = {
      top:  Math.max(16, Math.min(rect.top + rect.height / 2 - TOOLTIP_H / 2, window.innerHeight - TOOLTIP_H - 16)),
      left: Math.min(rect.right + 20, window.innerWidth - TOOLTIP_W - 16),
    };
  } else {
    posStyle = {
      top:  Math.max(16, rect.top - TOOLTIP_H - 16),
      left: Math.max(16, Math.min(rect.left + rect.width / 2 - TOOLTIP_W / 2, window.innerWidth - TOOLTIP_W - 16)),
    };
  }

  return (
    <div style={{ position: 'fixed', zIndex: 9001, width: TOOLTIP_W, maxWidth: 'calc(100vw - 32px)', ...posStyle }}>
      <TooltipContent
        step={step} stepIndex={stepIndex} total={total}
        onNext={onNext} onPrev={onPrev} onSkip={onSkip} darkMode={darkMode}
      />
    </div>
  );
};

// ── Componente principal ───────────────────────────────────────────
const TourGuide = ({ active, onDone, darkMode, steps: customSteps }) => {
  const ACTIVE_STEPS = customSteps || STEPS;
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect]           = useState(null);

  const step = ACTIVE_STEPS[stepIndex];

  const measureTarget = useCallback(() => {
    if (!step?.selector) { setRect(null); return; }
    const el = document.querySelector(step.selector);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [step?.selector]);

  useEffect(() => {
    if (!active) return;
    setRect(null);
    const t = setTimeout(measureTarget, 180);
    return () => clearTimeout(t);
  }, [active, stepIndex, measureTarget]);

  const handleNext = () => {
    if (stepIndex < ACTIVE_STEPS.length - 1) setStepIndex(i => i + 1);
    else handleDone();
  };
  const handlePrev = () => setStepIndex(i => Math.max(0, i - 1));
  const handleDone = () => {
    localStorage.setItem('condor360_tour_done', '1');
    setStepIndex(0);
    onDone();
  };

  if (!active) return null;

  return (
    <AnimatePresence>
      <div style={{ position: 'fixed', inset: 0, zIndex: 8999 }} onClick={handleNext}>
        <Spotlight rect={rect} />
        <div onClick={e => e.stopPropagation()}>
          <Tooltip
            step={step}
            stepIndex={stepIndex}
            total={ACTIVE_STEPS.length}
            rect={rect}
            onNext={handleNext}
            onPrev={handlePrev}
            onSkip={handleDone}
            darkMode={darkMode}
          />
        </div>
      </div>
    </AnimatePresence>
  );
};

export default TourGuide;

/*
════════════════════════════════════════════════════════════════════
  INTEGRACIÓN EN App.js
════════════════════════════════════════════════════════════════════

  1. import TourGuide from './components/TourGuide';

  2. const [showTour, setShowTour] = useState(false);

  3. useEffect(() => {
       if (currentUser && !localStorage.getItem('condor360_tour_done')) {
         const t = setTimeout(() => setShowTour(true), 1200);
         return () => clearTimeout(t);
       }
     }, [currentUser]);

  4. <TourGuide
       active={showTour}
       onDone={() => setShowTour(false)}
       darkMode={darkMode}
     />

  5. Atributos data-tour:
     data-tour="aircraft-selector"
     data-tour="checklist-item"
     data-tour="voice-btn"
     data-tour="admin-btn"

════════════════════════════════════════════════════════════════════
*/
